import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import AuthService from "./service/AmplifyAuth";
import TrustdService from "./service/TrustdService";
import { useAppContext } from "./context/AppContext";

export const apiHeaders = {
  "Content-Type": "application/json",
  "x-api-key": "Sh7yxT0sFO1aujXJhKfo537kU0RgYkwTNdybZWH2",
};

const STRIPE_USER_CACHE_TTL_MS = 2 * 60 * 1000;

const stripeUserCache = new Map();
const stripeUserInFlight = new Map();

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export function getCachedStripeUser(email, { maxAgeMs = STRIPE_USER_CACHE_TTL_MS } = {}) {
  const key = normalizeEmail(email);
  if (!key) return null;
  const entry = stripeUserCache.get(key);
  if (!entry?.data || !entry?.cachedAt) return null;
  if (Date.now() - entry.cachedAt > maxAgeMs) return null;
  return entry.data;
}

function setCachedStripeUser(email, data) {
  const key = normalizeEmail(email);
  if (!key || !data) return;
  stripeUserCache.set(key, {
    data,
    cachedAt: Date.now(),
  });
}

export function invalidateStripeUserCache(email) {
  const key = normalizeEmail(email);
  if (!key) return;
  stripeUserCache.delete(key);
  stripeUserInFlight.delete(key);
}

export async function getStripeUserByEmail(email, { forceRefresh = false, maxAgeMs = STRIPE_USER_CACHE_TTL_MS } = {}) {
  const key = normalizeEmail(email);
  if (!key) return null;

  if (!forceRefresh) {
    const cached = getCachedStripeUser(key, { maxAgeMs });
    if (cached) return cached;
    if (stripeUserInFlight.has(key)) return stripeUserInFlight.get(key);
  }

  const requestPromise = axios
    .get(`/v1/users/${key}`, { headers: apiHeaders })
    .then((response) => {
      const data = response?.data || null;
      if (data) setCachedStripeUser(key, data);
      return data;
    })
    .catch(() => null)
    .finally(() => {
      stripeUserInFlight.delete(key);
    });

  stripeUserInFlight.set(key, requestPromise);
  return requestPromise;
}

export function useToast() {
  return useAppContext().addToast;
}

export function useHostSummary() {
  const [summary, setSummary] = useState(
    { loading: true, username: "", memberSince: "", credit: 0, detailedUser: null },
  );

  const load = useCallback(async ({ forceRefresh = false } = {}) => {
    try {
      setSummary((current) => {
        // Keep rendering existing data while refreshing in the background.
        if (current?.username || current?.detailedUser) return current;
        return { ...current, loading: true };
      });

      const user = await AuthService.currentAuthenticatedUserAttributes();
      const cachedStripeUser = !forceRefresh ? getCachedStripeUser(user.username) : null;

      if (cachedStripeUser) {
        setSummary((current) => ({
          ...current,
          loading: false,
          username: current?.username || user.contactName || "",
          memberSince:
            current?.memberSince ||
            cachedStripeUser?.createdDate?.substring(0, 10) ||
            "",
          // Keep current credit until fresh user info arrives.
          credit: Number(current?.credit || 0),
          detailedUser: current?.detailedUser || cachedStripeUser,
        }));
      }

      const [userInfoResponse, stripeUser] = await Promise.all([
        TrustdService.getUserInformationByHostEmail({ forceRefresh: true }).catch(() => null),
        getStripeUserByEmail(user.username, { forceRefresh: true }).catch(() => null),
      ]);

      const nextSummary = {
        loading: false,
        username: user.contactName || "",
        memberSince:
          userInfoResponse?.createdDate?.substring(0, 10) ||
          stripeUser?.createdDate?.substring(0, 10) ||
          "",
        // Prefer the value coming from admin_hosts via /v1/users/:email.
        credit: Number(userInfoResponse?.screening ?? stripeUser?.screening ?? 0),
        detailedUser: userInfoResponse || stripeUser || null,
      };

      setSummary(nextSummary);
    } catch (error) {
      console.error("Error loading host summary:", error);
      setSummary((current) => ({ ...current, loading: false }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...summary,
    reload: () => load({ forceRefresh: true }),
  };
}

export function formatDate(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

export function TopInfoBar() {
  const { loading, username, memberSince, credit } = useHostSummary();
  if (loading && !username) return <div className="loading-container" />;

  const normalizedName = (username || "").trim();
  const firstName = normalizedName ? normalizedName.split(/\s+/)[0] : "Host";
  const initials = normalizedName
    ? normalizedName
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || "")
        .join("")
    : "TH";

  return (
    <div className="topInfoBar">
      <div className="dashboard-hero">
        <div className="dashboard-hero-main">
          <div className="dashboard-hero-avatar">{initials}</div>
          <div className="dashboard-hero-copy">
            <p className="dashboard-hero-kicker">Trustd Host Portal</p>
            <h2>Welcome back, {firstName}.</h2>
            <p className="dashboard-hero-text">Review your guest pipeline and run screenings quickly from one place.</p>
          </div>
        </div>
        <div className="dashboard-hero-stats">
          <div className="dashboard-hero-stat">
            <span className="dashboard-hero-stat-label">Screening Credits</span>
            <strong>{credit}</strong>
          </div>
          <div className="dashboard-hero-stat">
            <span className="dashboard-hero-stat-label">Member Since</span>
            <strong>{memberSince ? formatDate(memberSince) : "N/A"}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
